import React, { useState } from "react";
import { Checkbox, ActionIcon } from "@mantine/core";
import { IconEye } from "@tabler/icons-react";
import HighlightedText from "../HighlightedText";

const ROW_BACKGROUND = "#f2f2f2";

const previewButton = (onPreview) =>
	onPreview ? (
		<ActionIcon
			variant="subtle"
			size="sm"
			title="Preview"
			onClick={(e) => {
				e.stopPropagation();
				onPreview();
			}}
			style={{ flexShrink: 0, alignSelf: "center" }}
		>
			<IconEye size={16} />
		</ActionIcon>
	) : null;

// Generic mobile-style row shared by MattersBrowseList and
// MatterFolderBrowser: icon + title + one subtext line, an optional
// checkbox (omitted for folders, which are navigation-only), and an
// optional preview button. Mirrors AiAddContextModal's ResultRow, made
// reusable since Browse needs the same row shape at two different tiers.
// `highlightQuery`, when given, highlights matches of that text within the
// title. `subtitle` is rendered as-is — callers that want highlighting in
// it (see RowSubtitle) bake it in themselves, since parts of it (an ID
// text's label) deliberately shouldn't highlight.
//
// Matter rows pass BOTH onToggle (select as context) and onClick (navigate
// into the matter) — two different actions living on one row, so those get
// split into two independently clickable/hoverable zones: checkbox+icon on
// the left toggles selection, the name+info on the right navigates. Every
// other use of this row only ever gets one of the two, so it stays a
// single unified clickable/hoverable row exactly like before.
const BrowseRow = ({
	icon,
	title,
	subtitle,
	checked,
	disabled,
	onToggle,
	onClick,
	onPreview,
	highlightQuery,
}) => {
	const [hoveredZone, setHoveredZone] = useState(null);
	const titleAndSubtitle = (
		<div style={{ minWidth: 0, flex: 1 }}>
			<div
				style={{
					fontSize: 14,
					overflow: "hidden",
					textOverflow: "ellipsis",
					whiteSpace: "nowrap",
				}}
			>
				<HighlightedText text={title} query={highlightQuery} />
			</div>
			{subtitle ? (
				<div
					style={{
						fontSize: 12,
						color: "#666",
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
					}}
				>
					{subtitle}
				</div>
			) : null}
		</div>
	);

	if (onToggle && onClick) {
		return (
			<div
				style={{
					display: "flex",
					// stretch (the flex default) instead of center — so the
					// checkbox+icon zone's hover background always reaches
					// the full height of the row, even when the name+info
					// zone is taller (e.g. it has a subtitle line)
					gap: 8,
					// the checkbox/row zones carry their own left padding, but
					// there's nothing after the preview button to inset it
					// from the row's edge — match the 4px every other row
					// variant gets from its own outer `padding: "8px 4px"`
					paddingRight: 4,
					borderBottom: "1px solid var(--lw-light-grey, #eee)",
				}}
			>
				<div
					onClick={disabled ? undefined : onToggle}
					onMouseEnter={() => setHoveredZone("checkbox")}
					onMouseLeave={() => setHoveredZone(null)}
					style={{
						display: "flex",
						alignItems: "center",
						gap: 8,
						padding: "8px 4px",
						borderRadius: 4,
						cursor: disabled ? "default" : "pointer",
						background:
							hoveredZone === "checkbox" && !disabled
								? ROW_BACKGROUND
								: "transparent",
					}}
				>
					<Checkbox
						checked={!!checked}
						disabled={disabled}
						onChange={onToggle}
						onClick={(e) => e.stopPropagation()}
						style={{ flexShrink: 0 }}
					/>
					{icon}
				</div>
				<div
					onClick={disabled ? undefined : onClick}
					onMouseEnter={() => setHoveredZone("row")}
					onMouseLeave={() => setHoveredZone(null)}
					style={{
						display: "flex",
						alignItems: "center",
						flex: 1,
						minWidth: 0,
						padding: "8px 4px",
						borderRadius: 4,
						cursor: disabled ? "default" : "pointer",
						background:
							hoveredZone === "row" && !disabled
								? ROW_BACKGROUND
								: "transparent",
					}}
				>
					{titleAndSubtitle}
				</div>
				{previewButton(onPreview)}
			</div>
		);
	}

	const clickable = !!(onToggle || onClick);
	return (
		<div
			onClick={disabled ? undefined : onClick || onToggle}
			onMouseEnter={() => setHoveredZone("row")}
			onMouseLeave={() => setHoveredZone(null)}
			style={{
				display: "flex",
				alignItems: "center",
				gap: 8,
				padding: "8px 4px",
				borderBottom: "1px solid var(--lw-light-grey, #eee)",
				borderRadius: 4,
				cursor: disabled
					? "default"
					: clickable
						? "pointer"
						: "default",
				background:
					hoveredZone && !disabled ? ROW_BACKGROUND : "transparent",
			}}
		>
			{onToggle ? (
				<Checkbox
					checked={!!checked}
					disabled={disabled}
					onChange={onToggle}
					onClick={(e) => e.stopPropagation()}
					style={{ flexShrink: 0 }}
				/>
			) : (
				<span style={{ width: 18, flexShrink: 0 }} />
			)}
			{icon}
			{titleAndSubtitle}
			{previewButton(onPreview)}
		</div>
	);
};

export default BrowseRow;
